import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { faAngleDown, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Link } from 'react-router';
import { fade_in_opacity_keyframes, fade_in_opacity_top_keyframes } from '../../animations';
import { useHover } from '../../hooks/useHover';
import { MenuSubItemsWidget } from './MenuSubItemsWidget';

export interface MenuItem {
  to: string;
  label: string;
  tag?: string;
  tagVersion?: string;
  items?: MenuItem[];
  icon?: IconDefinition;
}

export interface MenuItemWidgetProps {
  item: MenuItem;
}

export const MenuItemWidget: React.FC<MenuItemWidgetProps> = (props) => {
  const { item } = props;
  const hasSubItems = item.items?.length > 0;

  const itemRef = React.useRef<HTMLDivElement>(null);

  const isHovering = useHover(itemRef, {
    leaveDelay: 150
  });

  return (
    <S.Item ref={itemRef}>
      <S.LinkInline to={item.to}>
        {item.label}
        {hasSubItems && <S.Angle icon={faAngleDown} />}
      </S.LinkInline>
      {hasSubItems && isHovering && (
        <S.Dropdown>
          <MenuSubItemsWidget items={item.items} />
        </S.Dropdown>
      )}
    </S.Item>
  );
};

namespace S {
  const fadeInOpacityAnim = css`
    animation-name: ${fade_in_opacity_keyframes(0.7)};
    animation-iteration-count: 1;
    animation-timing-function: ease-in;
    animation-duration: 0.5s;
  `;

  export const LinkInline = styled(Link)`
    text-decoration: none;
    min-height: ${(p) => p.theme.header.height}px;
    color: ${(p) => p.theme.header.textColor};
    padding-left: 10px;
    padding-right: 10px;
    line-height: ${(p) => p.theme.header.height}px;
    font-size: 14px;
    vertical-align: middle;
    opacity: 0.7;

    display: flex;
    align-items: center;
    column-gap: 5px;

    &:hover {
      opacity: 1;
    }
  `;

  export const Dropdown = styled.div`
    position: absolute;
    top: ${(p) => p.theme.header.height}px;
    animation-name: ${(p) => fade_in_opacity_top_keyframes(p.theme.header.height)};
    animation-iteration-count: 1;
    animation-timing-function: ease-in;
    animation-duration: 0.2s;
  `;

  export const Angle = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.header.textColor};
    font-size: 12px;
    opacity: 0.5;
  `;

  export const Item = styled.div`
    display: flex;
    align-items: stretch;
    position: relative;
    white-space: nowrap;

    ${fadeInOpacityAnim};
  `;
}
